import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import {
  DocumentTableRow,
  transformDocumentsToTableData,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/DocumentTableColumns'
import { usePaginatedRolodexEntries } from 'features/knowledgeDocumentViewer/hooks/usePaginatedRolodexEntries'
import debounce from 'lodash/debounce'
import { useEffect, useMemo, useState } from 'react'
import {
  RolodexAdvancedFilterOperator,
  RolodexAdvancedFilterType,
  RolodexEntryType,
  RolodexFilterOp,
  RolodexFilterType,
  SystemDocumentEntry,
} from 'types'

const DEBOUNCE_MS = 200

const COLUMN_ID_TO_ROLODEX_KEY: Record<string, string> = {
  title: 'title',
  documentTitle: 'document_title',
  system: 'system',
  vendor: 'vendor_name',
  docType: 'document_type',
  drawing: 'drawing_number',
  numPages: 'total_pages',
  revisionCode: 'revision_code',
  revisionDate: 'revision_date',
}

const SEARCHABLE_FIELDS = [
  'title',
  'document_title',
  'drawing_number',
  'system',
  'vendor_name',
  'document_type',
  'revision_code',
]

function escapeRegexString(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function convertColumnFiltersToRolodex(
  columnFilters: ColumnFiltersState
): RolodexFilterType[] {
  return columnFilters
    .filter((filter) => COLUMN_ID_TO_ROLODEX_KEY[filter.id])
    .map((filter) => {
      const rolodexKey = COLUMN_ID_TO_ROLODEX_KEY[filter.id]
      const value =
        typeof filter.value === 'string'
          ? escapeRegexString(filter.value)
          : filter.value
      return {
        field: 'data',
        key: rolodexKey,
        op: RolodexFilterOp.REGEX,
        value: String(value),
      }
    })
}

function convertSortingToRolodex(sorting: SortingState) {
  return sorting.map((sort) => {
    const rolodexKey = COLUMN_ID_TO_ROLODEX_KEY[sort.id] ?? sort.id
    return {
      field: 'data',
      key: rolodexKey,
      descending: sort.desc,
    }
  })
}

function buildTextSearchFilter(searchText: string): RolodexAdvancedFilterType {
  const escapedText = escapeRegexString(searchText)
  return {
    operator: RolodexAdvancedFilterOperator.OR,
    filters: SEARCHABLE_FIELDS.map((key) => ({
      field: 'data',
      key,
      op: RolodexFilterOp.REGEX,
      value: escapedText,
    })),
  }
}

interface UseKeywordSearchPaginationOptions {
  siteId: string | undefined
  searchText: string
  columnFilters: ColumnFiltersState
  sorting: SortingState
  pagination: PaginationState
  linkedDocumentIds?: string[]
}

interface UseKeywordSearchPaginationReturn {
  data: DocumentTableRow[]
  totalCount: number
  isLoading: boolean
}

export function useKeywordSearchPagination({
  siteId,
  searchText,
  columnFilters,
  sorting,
  pagination,
  linkedDocumentIds,
}: UseKeywordSearchPaginationOptions): UseKeywordSearchPaginationReturn {
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText)
  const [debouncedColumnFilters, setDebouncedColumnFilters] =
    useState(columnFilters)

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearchText(searchText)
    }, DEBOUNCE_MS)
    handler()
    return () => handler.cancel()
  }, [searchText])

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedColumnFilters(columnFilters)
    }, DEBOUNCE_MS)
    handler()
    return () => handler.cancel()
  }, [columnFilters])

  const rolodexFilters = useMemo(
    () => convertColumnFiltersToRolodex(debouncedColumnFilters),
    [debouncedColumnFilters]
  )

  const rolodexOrderBy = useMemo(
    () => convertSortingToRolodex(sorting),
    [sorting]
  )

  const advancedFilter = useMemo((): RolodexAdvancedFilterType | undefined => {
    const hasColumnFilters = rolodexFilters.length > 0
    const hasSearchText = Boolean(debouncedSearchText)

    if (!hasSearchText) {
      return undefined
    }

    if (hasColumnFilters) {
      return {
        operator: RolodexAdvancedFilterOperator.AND,
        filters: [
          ...rolodexFilters,
          buildTextSearchFilter(debouncedSearchText),
        ],
      }
    }

    return buildTextSearchFilter(debouncedSearchText)
  }, [rolodexFilters, debouncedSearchText])

  const loadOptions = useMemo(() => {
    const hasSearchText = Boolean(debouncedSearchText)
    const hasLinkedDocumentIds =
      linkedDocumentIds && linkedDocumentIds.length > 0
    return {
      type: [RolodexEntryType.SYSTEM_DOCUMENT],
      tags: { site: siteId ?? '' },
      ids: hasLinkedDocumentIds ? linkedDocumentIds : undefined,
      filters:
        !hasSearchText && rolodexFilters.length > 0
          ? rolodexFilters
          : undefined,
      order_by: rolodexOrderBy.length > 0 ? rolodexOrderBy : undefined,
      advanced_filter: advancedFilter,
      pagination,
    }
  }, [
    siteId,
    debouncedSearchText,
    rolodexFilters,
    advancedFilter,
    pagination,
    rolodexOrderBy,
    linkedDocumentIds,
  ])

  const {
    data: rawPaginatedEntries,
    totalCount,
    isLoading,
  } = usePaginatedRolodexEntries<SystemDocumentEntry>(loadOptions)

  const data = useMemo(
    () => transformDocumentsToTableData(rawPaginatedEntries),
    [rawPaginatedEntries]
  )

  return {
    data,
    totalCount,
    isLoading,
  }
}
